import { useContractWrite, usePrepareContractWrite, useNetwork, useWaitForTransaction } from 'wagmi';
import { getDexContracts } from '@/config/dex-v3-config';
import { pancakeswapV3FactoryABI } from '@/config/abis/pancakeswapV3Factory';
import { useToast } from '@/components/ui/use-toast';

/**
 * Hook to create a new PancakeSwap V3 pool
 */
export function useCreatePool(
  token0: string | undefined,
  token1: string | undefined,
  feeTier: number
) {
  const { chain } = useNetwork();
  const { toast } = useToast();
  const contracts = chain?.id ? getDexContracts(chain.id) : null;

  // Sort tokens to ensure correct order
  const sortedTokens = token0 && token1 && token0.toLowerCase() < token1.toLowerCase()
    ? [token0, token1]
    : [token1, token0];

  const { config } = usePrepareContractWrite({
    address: contracts?.factory as `0x${string}`,
    abi: pancakeswapV3FactoryABI,
    functionName: 'createPool',
    args: sortedTokens[0] && sortedTokens[1]
      ? [
          sortedTokens[0] as `0x${string}`,
          sortedTokens[1] as `0x${string}`,
          feeTier,
        ]
      : undefined,
    enabled: Boolean(contracts?.factory && sortedTokens[0] && sortedTokens[1]),
  });

  const { write, data, isLoading: isWriting } = useContractWrite({
    ...config,
    onSuccess: () => {
      toast({
        title: 'Transaction Submitted',
        description: 'Creating your liquidity pool...',
      });
    },
    onError: (error) => {
      toast({
        title: 'Pool Creation Failed',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const { isLoading: isConfirming, isSuccess } = useWaitForTransaction({
    hash: data?.hash,
    onSuccess: () => {
      toast({
        title: 'Pool Created!',
        description: 'Your liquidity pool has been created successfully.',
      });
    },
  });

  const createPool = () => {
    if (write) {
      write();
    }
  };

  return {
    createPool,
    isCreating: isWriting || isConfirming,
    isSuccess,
    isSupported: Boolean(contracts?.factory),
    canCreate: Boolean(write),
  };
}
